import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Factory, WindowsLogo } from "@phosphor-icons/react";

export default function Login() {
  const { login, register, formatApiError, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user && user !== false) navigate("/", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    // Surface SSO errors from the callback redirect, then strip params so refresh doesn't re-toast
    const p = new URLSearchParams(window.location.search);
    const err = p.get("sso_error");
    if (err) {
      toast.error(`Microsoft sign-in failed: ${err}${p.get("sso_desc") ? " — " + p.get("sso_desc") : ""}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // Detect whether SSO is wired
    fetch(`${API}/api/auth/microsoft/status`)
      .then((r) => r.json())
      .then((d) => setSsoEnabled(!!d.enabled))
      .catch(() => setSsoEnabled(false));
  }, [API]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="relative hidden lg:block overflow-hidden bg-navy">
        <img
          src="https://images.unsplash.com/photo-1581091212991-8891c7d4bd9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxtYW51ZmFjdHVyaW5nJTIwcGxhbnQlMjBpbnRlcmlvciUyMHdpZGUlMjBzaG90fGVufDB8fHx8MTc4NTc0NTM1MHww&ixlib=rb-4.1.0&q=85"
          alt="Manufacturing"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #0a1f5c 0%, #061544 100%)", opacity: 0.85 }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-4">
            <img
              src="/radish-logo.png"
              alt="Radish Technologies"
              className="h-11 w-auto bg-white/95 rounded-sm px-2 py-1"
              data-testid="radish-logo-hero"
            />
            <div className="w-px h-8 bg-cyan/40" />
            <div className="flex items-center gap-2">
              <Factory size={22} weight="duotone" className="text-cyan" />
              <div className="font-display text-lg tracking-tight">DOCUMENT HUB</div>
            </div>
          </div>
          <div>
            <div className="label-caps">Vertically Integrated · DMS</div>
            <h2 className="font-display text-5xl mt-4 leading-[0.95] uppercase">
              Documents<br/>
              <span className="headline-cyan">Built to spec.</span>
            </h2>
            <p className="mt-6 text-slate-300 max-w-md text-sm leading-relaxed">
              Versioned SOPs, quality certificates and work instructions — every revision, approval and expiry
              tracked with audit-ready traceability across your shop floor.
            </p>
          </div>
          <div className="label-caps">ISO 9001 · IATF 16949 · AS 9100 Ready</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div>
            <div className="label-caps">{mode === "login" ? "Sign in" : "Create account"}</div>
            <h1 className="font-display text-4xl mt-3 text-navy uppercase leading-none">
              {mode === "login" ? "Welcome back." : "Get started."}
            </h1>
            <p className="text-sm text-slate-500 mt-3">
              {mode === "login"
                ? "Access your document control workspace."
                : "First user becomes the workspace admin."}
            </p>
          </div>

          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-slate-600">Full name</Label>
              <Input
                id="name"
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Jane Operator"
                className="h-11 rounded-sm"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-600">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="auth-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@company.com"
              className="h-11 rounded-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-600">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="auth-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              minLength={6}
              className="h-11 rounded-sm"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="auth-submit-button"
            className="btn-navy w-full h-12"
          >
            {submitting ? "Please wait…" : mode === "login" ? "Sign in →" : "Create account →"}
          </button>

          {ssoEnabled && (
            <>
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-[10px] uppercase tracking-widest text-slate-400">or</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>
              <a
                href={`${API}/api/auth/microsoft/login`}
                data-testid="microsoft-sso-btn"
                className="w-full h-12 flex items-center justify-center gap-3 border border-navy text-navy bg-white hover:bg-slate-50 transition-colors rounded-sm text-sm font-semibold uppercase tracking-widest"
              >
                <WindowsLogo size={18} weight="fill" className="text-cyan" />
                Sign in with Microsoft
              </a>
            </>
          )}

          <div className="text-sm text-slate-500 text-center">
            Company employees: use Microsoft O365 sign-in. Local password login is reserved for the admin owner.
          </div>
        </form>
      </div>
    </div>
  );
}
